import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { subscriptionService } from '../../services/api';
import { getAppMarket } from '../../services/MarketService';

interface UpgradeModalProps {
    isOpen: boolean;
    onClose: () => void;
    reason?: string;
}

interface PlanDetails {
    name: string;
    price: number;
    description: string;
}

const UpgradeModal: React.FC<UpgradeModalProps> = ({ isOpen, onClose, reason }) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [planDetails, setPlanDetails] = useState<PlanDetails | null>(null);

    useEffect(() => {
        if (isOpen) {
            const fetchPlan = async () => {
                try {
                    // Fetch dynamic pricing
                    const res = await subscriptionService.getPlans();
                    // Assuming structure: { products: [ { plans: { pro: { price: 15, name: 'Landlord Pro' } } } ] }
                    // Adjust based on actual API response structure from ProductSelector context
                    const proPlan = res.data?.products?.[0]?.plans?.pro || { price: 15, name: 'Landlord Pro', description: 'Unlimited properties' };
                    setPlanDetails(proPlan);
                } catch (error) {
                    console.error("Failed to fetch plan details", error);
                    // Fallback
                    setPlanDetails({ price: 15, name: 'Landlord Pro', description: 'Unlimited properties' });
                }
            };
            fetchPlan();
        }
    }, [isOpen]);

    const handleUpgrade = async () => {
        setLoading(true);
        try {
            // /pricing is the real plan-selection page. /product-selector is
            // for users with multiple separate PRODUCTS (GDPR / Landlord /
            // Food Safety) and reads its options from navigate-state — when
            // hit via a bare URL it renders an empty page (white screen).
            navigate('/pricing?upgrade=1');
            onClose();
        } catch (error) {
            console.error("Navigation failed", error);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">

                {/* Background overlay */}
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} aria-hidden="true"></div>

                {/* Modal panel */}
                <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                    <div className="sm:flex sm:items-start">
                        <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
                            <i className="fas fa-crown text-yellow-600"></i>
                        </div>
                        <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                            <h3 className="text-lg leading-6 font-bold text-gray-900" id="modal-title">
                                Unlock Unlimited Potential
                            </h3>
                            <div className="mt-2">
                                <p className="text-sm text-gray-500 mb-4">
                                    {reason || `You've reached the limit of your Free plan. Upgrade to ${planDetails?.name} to manage more properties and access premium features.`}
                                </p>

                                <div className="bg-gray-50 rounded-lg p-4 mb-4 border border-gray-200">
                                    <h4 className="font-semibold text-gray-900 mb-2">Plan Comparison</h4>
                                    <div className="flex justify-between text-sm mb-2">
                                        <span className="text-gray-500">Free</span>
                                        <span className="font-medium">1 Property</span>
                                    </div>
                                    <div className="flex justify-between text-sm items-center">
                                        <span className="text-green-600 font-bold">{planDetails?.name}</span>
                                        <span className="font-bold text-gray-900">
                                            {planDetails ? `${getAppMarket() === 'texas' ? '$' : '£'}${planDetails.price}/mo` : 'Loading...'}
                                        </span>
                                    </div>
                                    <div className="mt-2 text-xs text-green-600">
                                        <i className="fas fa-check mr-1"></i> Unlimited Properties
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                        <button
                            type="button"
                            disabled={loading}
                            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                            onClick={handleUpgrade}
                        >
                            {loading ? 'Processing...' : 'Upgrade Now'}
                        </button>
                        <button
                            type="button"
                            className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:mt-0 sm:w-auto sm:text-sm"
                            onClick={onClose}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
export default UpgradeModal;
